import React from "react";

import { useTranslation } from "@refinedev/core";

import Close from "@mui/icons-material/Close";
import { Box, Button, Dialog, DialogActions, DialogContent, IconButton, Stack } from "@mui/material";

import { DATE_FORMAT_ISO, TIME_FORMAT_HH_MM } from "@/utils/date-utils";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { DatePicker } from "./date-picker";

dayjs.extend(utc);

interface CustomRangeDialogProps {
  open: boolean;
  onCancel: () => void;
  onApply: (start: Date, end: Date) => void;
  initialStartDate?: Date;
  initialEndDate?: Date;
  initialTimeStart?: string;
  initialTimeEnd?: string;
}

export function CustomRangeDialog({
  onCancel,
  onApply,
  open,
  initialStartDate,
  initialEndDate,
  initialTimeStart = "00:00",
  initialTimeEnd = "23:59",
}: CustomRangeDialogProps) {
  const { translate } = useTranslation();

  const [startDate, setStartDate] = React.useState<string>("");
  const [endDate, setEndDate] = React.useState<string>("");
  const [startTime, setStartTime] = React.useState<string>(initialTimeStart || "");
  const [endTime, setEndTime] = React.useState<string>(initialTimeEnd || "");

  // Pre-populate fields when dialog opens with existing custom dates
  React.useEffect(() => {
    if (open) {
      if (initialStartDate) {
        setStartDate(dayjs(initialStartDate).format(DATE_FORMAT_ISO));
        setStartTime(dayjs(initialStartDate).format(TIME_FORMAT_HH_MM));
      }
      if (initialEndDate) {
        setEndDate(dayjs(initialEndDate).format(DATE_FORMAT_ISO));
        setEndTime(dayjs(initialEndDate).format(TIME_FORMAT_HH_MM));
      }
    }
  }, [open, initialStartDate, initialEndDate]);

  const mergeDateTime = (dateStr: string, timeStr: string) =>
    dateStr && timeStr ? dayjs(`${dateStr}T${timeStr}`).toDate() : null;

  const handleSubmit = () => {
    const start = mergeDateTime(startDate, startTime);
    const end = mergeDateTime(endDate, endTime);

    if (!start || !end) {
      return;
    }

    onApply(dayjs(start).utc().toDate(), dayjs(end).utc().toDate());
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <IconButton
        onClick={onCancel}
        size="small"
        aria-label="close"
        sx={{ position: "absolute", top: 8, right: 8, color: "rgba(0, 0, 0, 0.54)" }}
      >
        <Close fontSize="small" />
      </IconButton>
      <DialogContent sx={{ overflowX: "hidden", pt: 5 }}>
        <Stack direction="row" spacing={2} sx={{ pt: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <DatePicker
              label={translate("trends.customRange.startDate")}
              name="startDate"
              type="date"
              max={endDate ? endDate : dayjs().format(DATE_FORMAT_ISO)}
              value={startDate}
              onChange={val => setStartDate(val || "")}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <DatePicker
              label={translate("trends.customRange.startTime")}
              name="startTime"
              type="time"
              value={startTime}
              onChange={val => setStartTime(val || "")}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <DatePicker
              label={translate("trends.customRange.endDate")}
              name="endDate"
              type="date"
              max={dayjs().format(DATE_FORMAT_ISO)}
              value={endDate}
              onChange={val => setEndDate(val || "")}
              min={startDate || undefined}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <DatePicker
              label={translate("trends.customRange.endTime")}
              name="endTime"
              type="time"
              value={endTime}
              onChange={val => setEndTime(val || "")}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "flex-end" }}>
        <Button variant="dialog" onClick={onCancel}>
          {translate("trends.customRange.cancel")}
        </Button>
        <Button variant="dialog" color="primary" onClick={handleSubmit}>
          {translate("trends.customRange.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
